import { useState } from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { HelpCircle, ChevronDown, ChevronRight, Search, RotateCcw } from 'lucide-react';

const ARTICLES = [
  {
    section: 'Getting Started',
    items: [
      { title: 'What is KSA ProHR?', body: 'KSA ProHR is your organization\'s HR management system. It handles leave management, expense tracking, travel requests, work scheduling, document management, and team visibility — all in one place.' },
      { title: 'First Login', body: 'When you first sign in with your Microsoft account, your profile is automatically created. You\'ll see your dashboard with leave balances and recent activity. If your balances show as empty, your admin needs to set up leave types and grant balances.' },
      { title: 'Navigating the App', body: 'Use the sidebar on the left to navigate between sections. The main areas are: Dashboard (overview), Planner (schedule WFH/leave), My Requests (leave history), Expenses (expense reports), Travel (travel requests), and Documents (contract & files).' },
    ],
  },
  {
    section: 'Work Planner',
    items: [
      { title: 'How the Planner Works', body: 'The Planner lets you visually schedule your work month. Select a schedule type from the right sidebar (Office, WFH, or a leave type), then click days on the calendar to apply it. Your changes are unsaved until you click "Save Plan."' },
      { title: 'Scheduling WFH / Remote', body: 'Select "Work from Home" from the Schedule Type panel, then click any working day. WFH days save immediately with no approval needed. Click a WFH day again to toggle it back to Office.' },
      { title: 'Planning Leave', body: 'Select a leave type (e.g. Vacation) from the Schedule Type panel, then click the days you want off. For multi-day leave, right-click a day to open the leave planner with start/end dates. Click "Save Plan" to open the checkout where you review and submit for approval.' },
      { title: 'The Checkout', body: 'When you Save Plan, a review window shows your WFH days and grouped leave requests. Consecutive leave days are automatically grouped into one request. Add a reason for each leave request, then submit for approval. WFH days save immediately.' },
    ],
  },
  {
    section: 'Leave Requests',
    items: [
      { title: 'Viewing Your Requests', body: 'Go to My Requests to see all your leave requests with their status: Pending (awaiting approval), Approved (confirmed), Rejected (declined), or Cancelled.' },
      { title: 'Cancelling Leave', body: 'You can cancel both pending and approved leave. Click on the request, then click "Cancel request." For approved leave, this restores your balance and removes the calendar event.' },
      { title: 'Leave Balances', body: 'Your dashboard shows remaining leave by type. The Planner sidebar also shows balances with real-time impact — as you plan leave, you can see how it affects your remaining days before submitting.' },
    ],
  },
  {
    section: 'Expense Reports',
    items: [
      { title: 'Creating an Expense Report', body: 'Go to Expenses → New Expense. Fill in the report details (purpose, location, project), then use the AI Receipt Scanner to upload receipt images. The AI extracts date, amount, vendor, and expense type automatically.' },
      { title: 'AI Receipt Scanner', body: 'Upload multiple receipt images at once. The AI scans each one and extracts the details. Click a scanned receipt to review/correct the extracted fields in a full-screen view with the receipt image on the right for reference. Click "Add to Report" to add it as a line item.' },
      { title: 'Submitting for Approval', body: 'After adding all line items, click "Review & Submit." This saves your report and sends it through the approval chain. You\'ll be notified by email when it\'s approved or if changes are needed.' },
    ],
  },
  {
    section: 'Travel Requests',
    items: [
      { title: 'Requesting Travel', body: 'Go to Travel → New Request. Enter your departure/destination, dates, reason, and upload your passport scan. Optionally list up to 3 preferred flights. Submit the request and the office manager will be emailed with all the details.' },
      { title: 'Tracking Your Travel', body: 'Go to My Travel to see all your requests. Once the office manager books your flight, the status changes to "Booked" and you\'ll see the confirmed airline, flight number, times, and booking reference.' },
    ],
  },
  {
    section: 'Documents',
    items: [
      { title: 'Your Contract', body: 'Go to My Contract to view and download your employment contract. If no contract is uploaded, contact your admin.' },
      { title: 'Company Documents', body: 'Go to My Documents to see organizational documents uploaded by HR (insurance policies, permits, etc.) with their expiry dates.' },
    ],
  },
  {
    section: 'Team Calendar & Overview',
    items: [
      { title: 'Team Calendar', body: 'Shows a monthly calendar view of everyone\'s leave (approved + pending), WFH days, and holidays. Filter by type. Pending leave shows with a * indicator.' },
      { title: 'Team Overview', body: 'Available to managers and admins. Shows a weekly or monthly Gantt-style view of each team member\'s schedule — who\'s in office, WFH, remote, or on leave.' },
    ],
  },
  {
    section: 'For Admins',
    items: [
      { title: 'Initial Setup', body: 'The setup wizard walks you through on first login. Key steps: set company settings, sync users from Microsoft Graph, seed leave types & email templates, import holidays, and configure approval chains.' },
      { title: 'Managing People', body: 'Admin → People: view all employees, assign roles (employee/approver/admin), set teams, departments, managers, and approval chains. Use "Sync from Microsoft Graph" to import new users.' },
      { title: 'Leave Types & Holidays', body: 'Admin → Leave Types: create/edit leave types with allowances and policies. Admin → Holidays: import public holiday packs or add individual holidays.' },
      { title: 'Approval Chains', body: 'Admin → Approval Chains: create multi-step chains (e.g. direct manager → department head). Assign chains to users in People. The global expense approval chain is set in Settings.' },
      { title: 'Expense Export Template', body: 'Admin → Expenses → Export Template: upload your company\'s XLSX template, map cells to KSA ProHR fields (type the cell reference like C6 next to each field). Then export any expense report into the filled template.' },
      { title: 'Contracts & Documents', body: 'Admin → Contracts: upload a contract per employee, set expiry dates and per-user notification days. Admin → Documents: track organizational documents with expiry reminders.' },
      { title: 'Travel Management', body: 'Admin → Travel Requests: view all travel requests, fill in booking details when flights are booked, cancel requests. Set the travel manager email in Settings.' },
      { title: 'Email & Notifications', body: 'Set the notification sender email in Settings (must be a valid M365 mailbox). Email templates can be customized in Admin → Email Templates. All notifications include a clickable "View in KSA ProHR" button.' },
      { title: 'Mailbox & Calendar Setup (first-time)', body: 'KSA ProHR uses ONE Microsoft 365 mailbox for both sending notification emails AND hosting the shared team calendar. Steps: (1) Create or pick a mailbox in M365 — a licensed user mailbox (e.g. hr@yourco.com) or a shared mailbox both work. (2) In Azure Portal → App registrations → KSA ProHR → API permissions, grant these APPLICATION (not delegated) permissions and click "Grant admin consent": Mail.Send, Calendars.ReadWrite, MailboxSettings.ReadWrite, User.Read.All, Directory.Read.All, Group.Read.All. (3) In KSA ProHR → Admin → Settings, paste the mailbox address into "Notification sender email" and save. (4) Test by approving a leave request — the requester should get an email and see the event land in their Outlook within seconds. The shared team calendar appears on the mailbox itself; share the subscribe link from Settings → Calendar Integration.' },
      { title: 'Does the notification mailbox need inbox privileges?', body: 'No. KSA ProHR only SENDS from the mailbox — it never reads the inbox. You do not need to forward, monitor, or auto-reply from it. Replies from recipients go back to whoever triggered the notification, not to KSA ProHR. A shared mailbox with no assigned license works fine as long as the app permissions above are granted to the KSA ProHR app registration.' },
      { title: 'Why is Outlook slow to show new leave events?', body: 'Outlook decides on its own schedule when to refresh subscribed internet calendars — typically every few hours, and it ignores the server\'s refresh hint. This is a Microsoft client-side behavior and applies to ANY subscribed ICS feed (including calendars hosted on Google). KSA ProHR already works around this: when a leave is approved, we push the event directly to the requester\'s personal Outlook via Microsoft Graph, so it appears within seconds. The shared team calendar uses M365\'s native sharing (not ICS polling) so it\'s also fast. The slow path only affects third-party clients (Apple Calendar, Google Calendar) subscribing to the team feed — those honor the 15-minute refresh hint.' },
    ],
  },
  {
    section: 'FAQ',
    items: [
      { title: 'I don\'t see any leave balances', body: 'Your admin needs to set up leave types (Admin → Leave Types → Seed sample data) and grant balances (Admin → Balances).' },
      { title: 'My leave request is stuck on Pending', body: 'It\'s waiting for your approver. Check with your manager or ask your admin to verify your approval chain is set up correctly.' },
      { title: 'I can\'t see the admin panel', body: 'Only users with the admin role can access /admin. Ask your admin to change your role in People → Edit User → Role.' },
      { title: 'Emails aren\'t being sent', body: 'The admin needs to configure: 1) Notification sender email in Settings, 2) Email templates (seed them via Dashboard → Seed sample data), 3) The sender must be a valid M365 mailbox with Graph Mail.Send permission.' },
      { title: 'How do I restart the guided tour?', body: 'Open your browser\'s developer tools (F12), go to Console, and type: localStorage.removeItem("prohr_employee_tour_done") — then reload the page.' },
    ],
  },
];

export default function HelpPage() {
  const { isAdmin } = useCurrentUser();
  const [search, setSearch] = useState('');
  const [openSection, setOpenSection] = useState(null);
  const [openArticle, setOpenArticle] = useState(null);

  const filtered = search
    ? ARTICLES.map((s) => ({
        ...s,
        items: s.items.filter((a) =>
          a.title.toLowerCase().includes(search.toLowerCase()) ||
          a.body.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((s) => s.items.length > 0)
    : ARTICLES.filter((s) => s.section === 'For Admins' ? isAdmin : true);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-primary" /> Help Center
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Guides and answers for every feature in KSA ProHR</p>
      </div>

      <div className="mb-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search help articles..."
            className="w-full bg-card border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/40"
          />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((section) => (
          <Card key={section.section}>
            <button
              onClick={() => setOpenSection(openSection === section.section ? null : section.section)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left"
            >
              <span className="text-sm font-semibold text-foreground">{section.section}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{section.items.length} article{section.items.length !== 1 ? 's' : ''}</span>
                {openSection === section.section ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </div>
            </button>
            {(openSection === section.section || search) && (
              <CardContent className="pt-0 pb-3">
                <div className="space-y-1">
                  {section.items.map((article) => {
                    const key = `${section.section}:${article.title}`;
                    const isOpen = openArticle === key || !!search;
                    return (
                      <div key={article.title}>
                        <button
                          onClick={() => setOpenArticle(isOpen && !search ? null : key)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-muted/50 transition-colors"
                        >
                          <ChevronRight className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          <span className="text-sm text-foreground">{article.title}</span>
                        </button>
                        {isOpen && (
                          <div className="ml-8 mr-3 mb-2 text-xs text-muted-foreground leading-relaxed">
                            {article.body}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      <div className="mt-6 text-center">
        <Button variant="ghost" size="sm" onClick={() => {
          try {
            localStorage.removeItem('prohr_employee_tour_done');
            localStorage.removeItem('prohr_setup_done');
          } catch {}
          window.location.reload();
        }}>
          <RotateCcw className="h-3.5 w-3.5" /> Restart Guided Tour
        </Button>
      </div>
    </div>
  );
}
